export function getFileExtension(fileName: string): string {
  if (!fileName) {
    return '';
  }

  const parts = fileName.split('.');

  if (parts.length < 2) {
    return '';
  }

  return parts.pop()!.toLowerCase();
}

export function getFileNameWithoutExtension(fileName: string): string {
  if (!fileName) {
    return '';
  }

  return fileName.substring(0, fileName.lastIndexOf('.'));
}

export function toFormData(files: FileList | File[]): FormData {
  const formData: FormData = new FormData();

  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < files.length; i++) {
    // The name must be 'files' because it must match with the parameter name in the asp core controller action
    formData.append('files', files[i], files[i].name);
  }

  return formData;
}

export function areFileFormatsAllowed(files: FileList | File[], allowedFileFormats: string[]): boolean {
  const length = files.length;
  for (let i = 0; i < length; i++) {
    const extension = getFileExtension(files[i].name);
    if (!allowedFileFormats.some((x) => x === extension)) {
      console.log(`Not allowed ext: ${extension}`);
      console.log(files[i]);
      return false;
    }
  }
  return true;
}

export function areFileNamesAllowed(files: FileList | File[], invalidChars: string[]): boolean {
  const length = files.length;
  for (let i = 0; i < length; i++) {
    if (!isFileNameAllowed(files[i].name, invalidChars)) {
      return false;
    }
  }
  return true;
}

export function isFileNameAllowed(filename: string, invalidChars: string[]): boolean {
  return invalidChars.every((c) => filename.indexOf(c) < 0);
}

export function areFilesEmpty(files: FileList | File[]): boolean {
  const length = files.length;
  for (let i = 0; i < length; i++) {
    if (files[i].size === 0) {
      return true;
    }
  }
  return false;
}

export interface FileValidationError {
  translationKey: string;
  interpolateParams?: object;
}

export type FileValidationResult = { valid: true } | { valid: false; error: FileValidationError };

export function areFilesValid(files: FileList | File[], invalidChars: string[], allowedFileFormats: string[]): FileValidationResult {
  if (!areFileNamesAllowed(files, invalidChars)) {
    return {
      valid: false,
      error: {
        translationKey: 'files.error.filename-not-allowed-message',
        interpolateParams: { invalidChars: invalidChars.join(' ') },
      },
    };
  } else if (!areFileFormatsAllowed(files, allowedFileFormats)) {
    return { valid: false, error: { translationKey: 'files.error.format-not-allowed-message' } };
  } else if (areFilesEmpty(files)) {
    return { valid: false, error: { translationKey: 'files.error.no-empty-files-allowed-message' } };
  } else {
    return { valid: true };
  }
}

export type FileType =
  | 'folder'
  | 'photo'
  | 'video'
  | 'audio'
  | 'text'
  | 'pdf'
  | 'zip'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'onenote'
  | 'other';

const fileTypeToSvgIconMapping: Record<FileType, string> = {
  folder: 'folder_icon',
  photo: 'photo_icon',
  video: 'video_icon',
  audio: 'audio_icon',
  text: 'text_icon',
  pdf: 'pdf_icon',
  zip: 'zip_icon',
  word: 'word_icon',
  excel: 'excel_icon',
  powerpoint: 'powerpoint_icon',
  onenote: 'onenote_icon',
  other: 'other_file',
};

export function getFileTypeFromExtension(extension: string): FileType {
  switch (extension.toLowerCase()) {
    case 'bmp':
    case 'gif':
    case 'jpeg':
    case 'jpg':
    case 'svg':
    case 'tif':
    case 'tiff':
    case 'png':
      return 'photo';
    case 'tex':
    case 'odt':
    case 'txt':
    case 'rtf':
    case 'key':
    case 'log':
    case 'vcf':
    case 'msg':
    case 'eml':
    case 'xml':
      return 'text';
    case 'xls':
    case 'xlsx':
    case 'xlsm':
    case 'xlsb':
    case 'csv':
      return 'excel';
    case 'one':
      return 'onenote';
    case 'doc':
    case 'docx':
      return 'word';
    case 'pdf':
      return 'pdf';
    case 'ppt':
    case 'pptx':
    case 'pps':
      return 'powerpoint';
    case 'avi':
    case 'mov':
    case 'mpg':
    case 'mpeg':
    case 'wmv':
    case 'flv':
    case 'f4v':
    case 'mp4':
      return 'video';
    case 'mp3':
    case 'wma':
    case 'wav':
    case 'acc':
    case 'm4a':
    case 'flac':
      return 'audio';
    case 'tar.gz':
    case 'tar':
    case 'zipx':
    case 'gz':
    case 'rar':
    case 'zip':
      return 'zip';
    case 'csb':
    case 'ext':
      return 'other';
    default:
      return 'other';
  }
}

export function getSvgIconFromFileType(fileType: FileType): string {
  return fileTypeToSvgIconMapping[fileType];
}

export function getFileIconName(extension: string): string {
  const fileType = getFileTypeFromExtension(extension);
  return getSvgIconFromFileType(fileType);
}

export function toFriendlyFileSize(size: number): string {
  const precision = (x: number) => {
    if (x > 100) {
      return 0;
    }
    if (x > 10) {
      return 1;
    }
    return 2;
  };

  if (size < 1024) {
    return size.toFixed(precision(size)).toString() + ' Bytes';
  }

  size = size / 1024;
  if (size < 1024) {
    return size.toFixed(precision(size)).toString() + ' KB';
  }

  size = size / 1024;
  if (size < 1024) {
    return size.toFixed(precision(size)).toString() + ' MB';
  }

  size = size / 1024;
  return size.toFixed(precision(size)).toString() + ' GB';
}
